import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { Span, Flex, FlexCenter, H1, ExpandMoreIcon } from '../../lib/components'
import './faq.scss'
import { useParams, useNavigate } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { faqServices } from '../../services/faq-service'
import { decodeFaqTitleParam, mapFaqResponseToSections, type FaqSectionView } from './faq-utils'

interface faqAccordionProps {
  question: string
  answerHtml: string
  isActive: boolean
  setIsActive: (val: boolean) => void
}

const Accordion = ({ question, answerHtml, isActive, setIsActive }: faqAccordionProps) => (
  <div className='accordion'>
    <div className='accordion-item'>
      <div className='accordion-title' onClick={() => setIsActive(!isActive)}>
        <div>{question}</div>
        <div className='accordion-icon'>
          <ExpandMoreIcon classes={isActive ? ['expand-more-icon'] : []} />
        </div>
      </div>
      {isActive && (
        <div
          className='accordion-content faq-answer-html'
          dangerouslySetInnerHTML={{ __html: answerHtml }}
        />
      )}
    </div>
  </div>
)

const FAQdetails = () => {
  const [sections, setSections] = useState<FaqSectionView[]>([])
  const [selected, setSelected] = useState<FaqSectionView | null>(null)
  const [isActive, setIsActive] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const navigate = useNavigate()
  const params = useParams()
  const { title: titleParam } = params

  const load = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const data = await faqServices.getFaqs()
      setSections(mapFaqResponseToSections(data))
    } catch {
      setLoadError('We could not load FAQs. Please try again later.')
      setSections([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!sections.length) {
      setSelected(null)
      return
    }
    const decoded = decodeFaqTitleParam(titleParam)
    const match = sections.find((s) => s.title === decoded)
    setSelected(match ?? sections[0])
    setIsActive(false)
    setActiveIndex(null)
  }, [sections, titleParam])

  const handleClick = (section: FaqSectionView) => {
    navigate(`/faq-details/${encodeURIComponent(section.title)}`)
    setSelected(section)
    setIsActive(false)
    setActiveIndex(null)
  }

  const decodedBreadcrumb = decodeFaqTitleParam(titleParam)

  return (
    <>
      <Flex classes={['faq-content-wrapper']}>
        <>
          <Flex classes={['faq-navigation']} dataTestId='faq-navigation-breadcrumbs'>
            <div className='pointer' onClick={() => navigate('/home')}>
              <Span text='Home' />
            </div>
            <Span classes={['breadcrumbs-arrow']} text='>' />
            <div className='pointer' onClick={() => navigate('/faq')}>
              <Span text='FAQ' />
            </div>
            <Span classes={['breadcrumbs-arrow']} text='>' />
            <div>
              <Span
                classes={['faq-selected-tab']}
                text={decodedBreadcrumb || selected?.title || ''}
              />
            </div>
          </Flex>
          <FlexCenter classes={['faq-details-heading']}>
            <H1 text='FAQ' classes={['faq-details-title']} />
          </FlexCenter>
        </>
      </Flex>
      {isLoading && (
        <FlexCenter classes={['faq-details-loading']}>
          <CircularProgress size={40} color='primary' />
        </FlexCenter>
      )}
      {!isLoading && loadError && (
        <FlexCenter classes={['faq-page-error mb-82']}>
          <Span text={loadError} classes={['faq-page-error-text']} />
        </FlexCenter>
      )}
      {!isLoading && !loadError && sections.length === 0 && (
        <FlexCenter classes={['faq-page-empty mb-82']}>
          <Span text='No FAQ content is available yet.' classes={['faq-page-empty-text']} />
        </FlexCenter>
      )}
      {!isLoading && !loadError && sections.length > 0 && (
        <Flex variant='space-between' classes={['faq-container', 'mt-72', 'mb-82']}>
          <Flex variant='column' classes={['faq-side-navigation']} dataTestId='faq-sections-tabs'>
            <>
              {sections.map((section) => (
                <div
                  key={section.title}
                  className={
                    section.title === selected?.title
                      ? 'faq-navigation-list faq-selected-container'
                      : 'faq-navigation-list'
                  }
                  onClick={() => handleClick(section)}
                >
                  <Flex variant='row'>
                    <div className='faq-details-icon'>{section.icon as ReactNode}</div>
                    <Span classes={['faq-list-item', 'pointer']} text={section.title as string} />
                  </Flex>
                </div>
              ))}
            </>
          </Flex>
          <Flex variant='column' classes={['faq-accordion', 'ml-59']}>
            {selected?.details.map((row, index) => (
              <Accordion
                key={row.id}
                question={row.question}
                answerHtml={row.answerHtml}
                isActive={activeIndex === index ? isActive : false}
                setIsActive={(val: boolean) => {
                  setActiveIndex(index)
                  setIsActive(val)
                }}
              />
            ))}
          </Flex>
        </Flex>
      )}
    </>
  )
}

export { FAQdetails }
